/**
 * Centre-screen combat feedback, all on ONE small 2D canvas that is only
 * redrawn when its state actually changes:
 *   - dynamic four-blade crosshair driven by ctx.weapons.spread
 *   - hit markers (body / headshot / kill)
 *   - arced damage-direction indicators
 *   - low-health heartbeat ring
 *
 * A DOM node per blade would be 4-30 nodes and a style recalc per frame; a
 * 520 px canvas costs one clearRect and a handful of fills.
 */
import { COL, clamp, clamp01, ease, rgba, fitCanvas, clearCanvas, devicePx } from './Theme.js';

export const HM_BODY = 0, HM_HEAD = 1, HM_KILL = 2;
const HM_DUR = 0.12;      // full hit-marker life, seconds
const DMG_DUR = 1.2;      // damage indicator life, seconds

export const SIZE = 560;  // css px, square, centred

export class Reticle {
  constructor(root) {
    this.cv = document.createElement('canvas');
    this.cv.className = 'hud-c hud-reticle';
    root.appendChild(this.cv);
    this.g = fitCanvas(this.cv, SIZE, SIZE);
    this.dirty = true;

    this.hits = [];        // { t, kind }
    this.dmg = [];         // { t, angle }  angle = radians, 0 = straight ahead
    this.firePunch = 0;    // 0..1, decays

    // Tunables exposed to the settings panel.
    this.opt = {
      dot: true,
      opacity: 1,
      thickness: 2,
      bladeLen: 9,
      baseGap: 5,
      hitMarkers: true,
      damageIndicators: true,
    };
  }

  resize(dpr = devicePx()) {
    this.g = fitCanvas(this.cv, SIZE, SIZE, dpr);
    this.dirty = true;
  }

  hit(kind) {
    if (!this.opt.hitMarkers) return;
    this.hits.push({ t: 0, kind });
    if (this.hits.length > 4) this.hits.shift();
    this.dirty = true;
  }

  damage(angle) {
    if (!this.opt.damageIndicators) return;
    // Merge with a recent indicator from nearly the same bearing so a burst of
    // hits reads as one strengthening arc instead of stacked duplicates.
    for (const d of this.dmg) {
      if (Math.abs(shortAngle(d.angle - angle)) < 0.28 && d.t < 0.5) {
        d.t = 0;
        this.dirty = true;
        return;
      }
    }
    this.dmg.push({ t: 0, angle });
    if (this.dmg.length > 6) this.dmg.shift();
    this.dirty = true;
  }

  fired() {
    this.firePunch = 1;
    this.dirty = true;
  }

  advance(dt) {
    if (this.firePunch > 0) {
      this.firePunch = Math.max(0, this.firePunch - dt * 9);
      this.dirty = true;
    }
    for (let i = this.hits.length - 1; i >= 0; i--) {
      this.hits[i].t += dt;
      this.dirty = true;
      if (this.hits[i].t > HM_DUR) this.hits.splice(i, 1);
    }
    for (let i = this.dmg.length - 1; i >= 0; i--) {
      this.dmg[i].t += dt;
      this.dirty = true;
      if (this.dmg[i].t > DMG_DUR) this.dmg.splice(i, 1);
    }
  }

  /**
   * @param s HUD state (spread in radians, ads 0..1, health, pxPerRad, etc.)
   */
  draw(s) {
    const g = this.g;
    clearCanvas(g);
    const c = SIZE / 2;
    const dpr = g.__dpr;

    /* ---------------------------------------------------------- crosshair */
    const adsFade = 1 - smooth(0.30, 0.82, s.ads);
    const chA = adsFade * this.opt.opacity;
    if (chA > 0.004) {
      const spreadPx = Math.tan(Math.max(0, s.spread)) * s.pxPerRad;
      const gap = clamp(this.opt.baseGap + spreadPx, 3, 210) + this.firePunch * 3.5;
      const len = this.opt.bladeLen + this.firePunch * 2.5;
      const th = this.opt.thickness;

      g.globalAlpha = chA;
      // Blades: dark halo first so the reticle survives a bright sky.
      for (let pass = 0; pass < 2; pass++) {
        const o = pass === 0 ? 1 : 0;
        g.fillStyle = pass === 0 ? 'rgba(0,0,0,0.72)' : COL.white;
        // right, left, down, up
        rect(g, c + gap - o, c - th / 2 - o, len + o * 2, th + o * 2, dpr);
        rect(g, c - gap - len - o, c - th / 2 - o, len + o * 2, th + o * 2, dpr);
        rect(g, c - th / 2 - o, c + gap - o, th + o * 2, len + o * 2, dpr);
        rect(g, c - th / 2 - o, c - gap - len - o, th + o * 2, len + o * 2, dpr);
      }
      if (this.opt.dot) {
        g.fillStyle = rgba('#000000', 0.72);
        rect(g, c - 2.5, c - 2.5, 5, 5, dpr);
        g.fillStyle = '#FFFFFF';
        rect(g, c - 1.5, c - 1.5, 3, 3, dpr);
      }
      g.globalAlpha = 1;
    }

    /* ------------------------------------------------------- hit markers */
    for (const h of this.hits) drawHitMarker(g, c, h, dpr);

    /* -------------------------------------------------- damage direction */
    // `s.heading` is the player's world bearing in radians (0 = -Z = north),
    // the same convention `damage()` angles are stored in.
    for (const d of this.dmg) drawDamageArc(g, c, d, s.heading);

    /* ------------------------------------------------------- low health */
    if (s.lowHealth > 0.001) drawHeartbeat(g, c, s.lowHealth, s.beat);

    this.dirty = false;
  }
}

/* ------------------------------------------------------------- internals */

function rect(g, x, y, w, h, dpr) {
  const x0 = Math.round(x * dpr) / dpr;
  const y0 = Math.round(y * dpr) / dpr;
  g.fillRect(x0, y0, Math.round(w * dpr) / dpr, Math.round(h * dpr) / dpr);
}

function smooth(e0, e1, x) {
  const t = clamp01((x - e0) / (e1 - e0));
  return t * t * (3 - 2 * t);
}

function shortAngle(a) {
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}

/**
 * Four-tick X. `snap` drives an over-shooting scale-in in the first ~35 ms,
 * `out` drives the scale-out + fade across the remainder. Peak readability is
 * at p ≈ 0.30 (about 36 ms in) — that is what the ui_hitmarker shot freezes on.
 */
function drawHitMarker(g, c, h, dpr) {
  const p = clamp01(h.t / HM_DUR);
  const snap = ease.outBack(clamp01(p / 0.28));
  const out = ease.outCubic(clamp01((p - 0.30) / 0.70));
  const scale = 0.58 + 0.42 * snap + 0.50 * out;
  const alpha = 1 - out * out;
  if (alpha <= 0.004) return;

  const kill = h.kind === HM_KILL;
  const head = h.kind === HM_HEAD;
  const col = kill ? COL.kill : COL.white;
  const th = kill ? 3.4 : head ? 3 : 2.4;
  const r0 = (head ? 7.5 : 7) * scale;
  const len = (kill ? 13 : head ? 12 : 10.5) * scale;

  g.save();
  g.globalAlpha = alpha;
  g.translate(c, c);
  g.lineCap = 'butt';

  const ticks = (rot, inner, length, width, style) => {
    g.lineWidth = width;
    g.strokeStyle = style;
    for (let i = 0; i < 4; i++) {
      const a = rot + i * Math.PI / 2;
      const cx = Math.cos(a), sy = Math.sin(a);
      g.beginPath();
      g.moveTo(cx * inner, sy * inner);
      g.lineTo(cx * (inner + length), sy * (inner + length));
      g.stroke();
    }
  };

  // Halo pass, then the marker.
  ticks(Math.PI / 4, r0, len, th + 2.4, rgba('#000000', 0.62));
  ticks(Math.PI / 4, r0, len, th, col);

  if (head) {
    // Headshot: a second, shorter set on the cardinal axes turns the X into an
    // 8-point star — instantly distinguishable in peripheral vision.
    ticks(0, r0 + 1, len * 0.46, th + 2.2, rgba('#000000', 0.62));
    ticks(0, r0 + 1, len * 0.46, th * 0.82, col);
    g.lineWidth = 1.4;
    g.strokeStyle = rgba(COL.white, 0.5 * alpha);
    g.beginPath(); g.arc(0, 0, r0 + len * 0.82, 0, Math.PI * 2); g.stroke();
  }
  if (kill) {
    // Kill: an expanding thin ring reading as a "confirm" pulse.
    const rr = (r0 + len) * (1 + out * 0.9);
    g.lineWidth = 1.6;
    g.strokeStyle = rgba(COL.kill, 0.55 * (1 - out));
    g.beginPath(); g.arc(0, 0, rr, 0, Math.PI * 2); g.stroke();
    ticks(Math.PI / 4, r0, len * 0.5, th + 1.2, rgba(COL.enemyDeep, 0.9));
  }
  g.restore();
}

const DMG_R = 124;      // radius of the arc band, css px
const DMG_HALF = 0.29;  // half angular width, radians (≈33° total)

function drawDamageArc(g, c, d, yaw) {
  const p = clamp01(d.t / DMG_DUR);
  const a = Math.pow(1 - p, 1.5);
  if (a <= 0.004) return;
  // Screen bearing: 0 = up (straight ahead), positive = clockwise.
  const rel = shortAngle(d.angle - yaw);
  const scr = rel - Math.PI / 2; // canvas angles start at +X
  const r = DMG_R + ease.outCubic(p) * 22;
  const half = DMG_HALF * (1 - ease.outQuart(p) * 0.22);

  g.save();
  g.translate(c, c);
  g.globalAlpha = a;
  g.lineCap = 'butt';

  // Dark outline so it reads over sky and over muzzle flash alike, then a
  // solid red body with the ends tapered by two shorter passes.
  g.lineWidth = 16;
  g.strokeStyle = rgba('#000000', 0.52);
  g.beginPath(); g.arc(0, 0, r, scr - half, scr + half); g.stroke();
  g.lineWidth = 12;
  g.strokeStyle = rgba(COL.enemyDeep, 0.9);
  g.beginPath(); g.arc(0, 0, r, scr - half, scr + half); g.stroke();
  g.lineWidth = 12;
  g.strokeStyle = rgba(COL.enemy, 0.96);
  g.beginPath(); g.arc(0, 0, r, scr - half * 0.80, scr + half * 0.80); g.stroke();
  g.lineWidth = 12;
  g.strokeStyle = rgba('#FF8A7A', 0.85);
  g.beginPath(); g.arc(0, 0, r, scr - half * 0.34, scr + half * 0.34); g.stroke();

  // Outward chevron at the centre of the arc — reads as "it came from here".
  g.translate(Math.cos(scr) * (r + 16), Math.sin(scr) * (r + 16));
  g.rotate(scr + Math.PI / 2);
  g.fillStyle = rgba('#000000', 0.5);
  g.beginPath();
  g.moveTo(0, -9); g.lineTo(9, 5); g.lineTo(0, 1); g.lineTo(-9, 5);
  g.closePath(); g.fill();
  g.fillStyle = rgba(COL.enemy, 0.95);
  g.beginPath();
  g.moveTo(0, -7); g.lineTo(7, 4); g.lineTo(0, 0.6); g.lineTo(-7, 4);
  g.closePath(); g.fill();
  g.restore();
}

/** Thin ring around the reticle that thumps with the heart rate. */
function drawHeartbeat(g, c, intensity, beat) {
  const r = 26 + beat * 7;
  g.save();
  g.translate(c, c);
  g.lineWidth = 1.5 + beat * 1.6;
  g.strokeStyle = rgba(COL.enemy, (0.10 + beat * 0.34) * intensity);
  g.lineCap = 'butt';
  for (let i = 0; i < 4; i++) {
    const a0 = i * Math.PI / 2 + Math.PI / 4 - 0.50;
    g.beginPath();
    g.arc(0, 0, r, a0, a0 + 1.0);
    g.stroke();
  }
  g.restore();
}
